import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import Autocomplete from '@mui/material/Autocomplete';
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { debounce } from "lodash";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import MarkAsPickedUpDialog from "../components/MarkAsPickedUpDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 10;
const PICKED_UP = "Picked Up";
const PENDING = "Pending";

const columns = [
  {
    field: "order_number",
    headerName: "Order #",
    width: 120,
    sortable: false,
  },
  {
    field: "order_created_at",
    headerName: "Order Date",
    width: 150,
    sortable: false,
    valueGetter: (value) =>
      value
        ? format(new Date(value), "yyyy-MM-dd")
        : "",
  },
  {
    field: "customer_name",
    headerName: "Customer",
    width: 170,
    sortable: false,
  },
  {
    field: "customer_email",
    headerName: "Email",
    width: 220,
    sortable: false,
  },
  {
    field: "product_count",
    headerName: "Product Count",
    width: 150,
    sortable: false,
  },
  {
    field: "order_status",
    headerName: "Status",
    width: 120,
    sortable: false,
    renderCell: (params) => {
      let rootClass;
      if (params?.row?.order_status === PENDING) {
        rootClass = "pending";
      } else if (params?.row?.order_status === PICKED_UP) {
        rootClass = "withdrawn";
      }
      return <Chip label={params?.row?.order_status} classes={{ root: rootClass }} />;
    },
  },
  {
    field: "picked_up_at",
    headerName: "Pickup date",
    width: 200,
    sortable: false,
    valueGetter: (value) =>
      value
        ? format(new Date(value), "yyyy-MM-dd")
        : "",
  },
];

export default function ShopifyOrders() {
  const [filter, setFilter] = useState({
    orderNumber: "",
    customer: {
      id: null,
      first_name: "",
      last_name: "",
      email: "",
    },
  });
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [dialogVisible, setDialogVisible] = useState(false);
  const [customers, setCustomers] = useState([]);

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);


  let active = useRef(false);

  const showDialog = () => setDialogVisible(true);
  const hideDialog = (reload) => {
    setDialogVisible(false);
    if (reload) {
      loadOrders();
    }
  }

  const onConfirm = async (comment) => {
    setIsLoading(true);
    const data = {
      ids: selectionModel.ids ? Array.from(selectionModel.ids) : [],
      comment,
    };
    const response = await fetchWithInterceptor("/api/shopify/mark-as-pickedup", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        'x-access-token': authContext.getToken()
      },
    });
    if (response.ok) {
      hideDialog(true);
      setSelectionModel({ type: 'include', ids: new Set() });
    } else {
      appContext.showMessage(
        "There was an error updating the order",
        "error"
      );
    }
    setIsLoading(false);
  };

  const loadOrders = async () => {
    if (isLoading) {
      return;
    }
    if (!filter.orderNumber && !filter.customer?.email) {
      setOrders([]);
      return;
    }
    setIsLoading(true);
    const params = new URLSearchParams({
      orderNumber: filter?.orderNumber ?? '',
      customerEmail: filter?.customer?.email ?? '',
    });
    const response = await fetchWithInterceptor(
      `/api/shopify/orders/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }

      const data = await response.json();
      setOrders(data);

    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the orders",
        "error"
      );
    }
    setIsLoading(false);
  };

  const searchCustomers = async (inputValue) => {
    if (!inputValue) {
      setCustomers([]);
      return;
    }
    const params = new URLSearchParams({
      filter: inputValue
    });
    const response = await fetchWithInterceptor(
      `/api/shopify/customers/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }
      const data = await response.json();
      setCustomers(data);
    }
  };

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  const debouncedLoadOrders = debounce(loadOrders, 300);

  const onKeyPress = (e) => {
    if (e.key === "Enter") {
      debouncedLoadOrders(true);
    }
  };

  useEffect(() => {
    debouncedLoadOrders(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.customer]);

  // Bajada de texto antes del Container principal
  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Manage in-store pickup orders by tracking their status and marking them as delivered. Use this module to ensure local customers receive their wine, spirits, and beer promptly.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ paddingBottom: 3 }}>
      <Paper sx={{ width: "100%", backgroundColor: "background.paper", mt: 2, mb: 2 }}>
        <div style={{ padding: 16 }}>
          <Typography variant="h6">Search Filters</Typography>
          <br />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Order #"
                placeholder="Eg: 99001"
                variant="outlined"
                value={filter.orderNumber}
                onChange={(e) =>
                  setFilter({ ...filter, orderNumber: e.target.value })
                }
                color="primary"
                helperText="Type the order # and press Enter"
                fullWidth
                onKeyDown={onKeyPress}
              />
            </Grid>
            <Grid sx={{ width: "50%" }}>
              <Autocomplete
                onChange={(event, newValue) => setFilter({ ...filter, customer: newValue })}
                onInputChange={(event, newInputValue) => searchCustomers(newInputValue)}
                options={customers}
                getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''} ${option.email ? `(${option.email})` : ''}`}
                renderInput={(params) => <TextField {...params} label="Customer" placeholder="Ej: John" variant="outlined" />}
                fullWidth
              />
            </Grid>
          </Grid>
        </div>
        <Divider />

        <Grid container justifyContent="flex-end" style={{ padding: 16 }}>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={showDialog}
              startIcon={<CheckIcon />}
              disabled={!selectionModel.ids.size}
            >
              Mark as picked up
            </Button>
          </Grid>
        </Grid>
        <DataGrid
          rows={orders}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel(model)}
          isRowSelectable={(params) => params?.row?.order_status !== PICKED_UP}
          disableSelectionOnClick
          disableColumnFilter
          loading={isLoading}
          sx={{
            height: 52 * PAGE_SIZE + 120,
            border: "none",
            "& .MuiDataGrid-menuIcon": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.75rem",
              color: "rgba(58, 53, 65, 0.87)",
            },
            "& .MuiDataGrid-columnsContainer": {
              backgroundColor: "rgb(249, 250, 252)",
            },
            "& .MuiChip-root.pending": {
              color: "rgb(102, 60, 0)",
              backgroundColor: "rgb(255, 244, 229)",
            },
            "& .MuiChip-root.withdrawn": {
              color: "rgb(30, 70, 32)",
              backgroundColor: "rgb(237, 247, 237)",
            },
          }}
        />
      </Paper>
      <MarkAsPickedUpDialog open={dialogVisible} onClose={hideDialog} onConfirm={onConfirm} orders={Array.from(selectionModel.ids)} />
    </Container>
    </>
  );
}